import {FlowBlock, FlowProperty, FlowValidationError} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {VisualStreamIds} from '@mintdata-wsp/common/state/ui/visualDesigner/visualData/VisualStreamIds';
import {CloudCategory, PropertyWidgetName} from '@mintdata-wsp/common/state/ui/visualDesigner/VisualDesignerState';
import {NameUtil} from '@mintdata-wsp/common/util/NameUtil';
import {WSPIcon} from '@mintdata-wsp/resources/icons/WSPIcon';
import {WSPIcons, WSPIconSize} from '@mintdata-wsp/resources/icons/WSPIcons';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Scrollable, ScrollBehavior} from '@mintdata-wsp/wsp/ui/components/scrollable/Scrollable';
import {FlowPropertiesUtils} from '@mintdata-wsp/wsp/ui/views/visualDesigner/flow/panels/properties/FlowPropertiesUtils';
import {PropertyBlockLabel} from '@mintdata-wsp/wsp/ui/views/visualDesigner/flow/panels/properties/widgets/common/PropertyBlockLabel';
import {ComponentInfo} from '@mintdata-wsp/wsp/ui/views/visualDesigner/panels/properties/ComponentInfo';
import {PropertiesPanel} from '@mintdata-wsp/wsp/ui/views/visualDesigner/panels/properties/PropertiesPanel';
import {Col} from '@mintdata/mint-kit/impl/properties/components/col/Col';
import {Collapse} from '@mintdata/mint-kit/impl/properties/components/collapse/Collapse';
import {InlineLabel} from '@mintdata/mint-kit/impl/properties/components/inlineLabel/InlineLabel';
import {Row} from '@mintdata/mint-kit/impl/properties/components/row/Row';

import {Section} from '@mintdata/mint-kit/impl/properties/components/section/Section';
import * as classnames from 'classnames/bind';
import {action, observable} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';

const styles = require('@mintdata-wsp/wsp/ui/views/visualDesigner/flow/panels/properties/FlowPropertiesPanel.less');
const cx = classnames.bind(styles);

interface CategoryWithProps
{
    label:string;
    key:string;
    props:FlowProperty[];
}

interface widgetRef
{
    ref:HTMLElement;
    propName:string;
}

interface FileDropzoneCtx
{
    isDraggingOver:boolean;
    isLoading:boolean;
    errorMsg:string;
}

export interface IFlowPropertiesPanelState
{
    fileDropzoneCtx:FileDropzoneCtx;
}

@observer
export class FlowPropertiesPanel extends React.Component<any, IFlowPropertiesPanelState>
{
    @observable
    private _state:IFlowPropertiesPanelState = {
        fileDropzoneCtx: {
            isDraggingOver: false,
            isLoading: false,
            errorMsg: null
        }
    };

    private _widgetRefs:widgetRef[] = [];
    private _fileInputRef:HTMLElement | null;

    private _refHandlers = {
        forWidget: (propName:string) =>
        {
            return (ref:HTMLElement | null) =>
            {
                if( ref )
                {
                    this._widgetRefs.push({propName, ref});
                }
                else
                {
                    this._widgetRefs = this._widgetRefs.filter(item => item.propName !== propName);
                }
            };
        },
        fileInput: (ref:HTMLElement | null) => this._fileInputRef = ref
    };

    public componentDidUpdate()
    {
        const fppController = controllers.ui.flowPropertiesPanel;
        const fsController = controllers.ui.flowSelection;

        const propertyToFocus = fppController.getPropertyToFocus();

        if( propertyToFocus )
        {
            const block = fsController.getAnySingleSelectedBlock();

            const property = this._widgetRefs.find(({propName}) => (
                fppController.isPropertyToFocus(block.id, propName)
            ));

            if( property )
            {
                property.ref.focus();
                fppController.setDoneFocusingProperty();
            }
        }
    }

    public render()
    {
        const selectedBlocks = controllers.ui.flowSelection.getSelectedBlocks();

        return (
            <Scrollable isScrollX={true} isScrollY={true} behavior={ScrollBehavior.OnHover}>
                <div className={cx('base')}>
                    <PropertiesPanel>
                        {
                            selectedBlocks.length === 0 &&
                            this._renderNoComponentSelected()
                        }
                        {
                            selectedBlocks.length > 1 &&
                            this._renderMultipleComponentsSelected()
                        }
                        {
                            selectedBlocks.length === 1 &&
                            this._renderProps(selectedBlocks[0])
                        }
                    </PropertiesPanel>
                </div>
            </Scrollable>
        );
    }

    private _onClickIconDoc(e:React.MouseEvent<HTMLElement>, componentName:string):void
    {
        e.stopPropagation();
        e.preventDefault();

        const dashedName = NameUtil.getDashedName(componentName);

        const url = 'https://mintdata.com/docs/reference/blocks/' + dashedName;

        window.open(url, '_blank').focus();
    }

    private _renderProps(block:FlowBlock)
    {
        const {properties, component} = block;

        const componentName = NameUtil.getDisplayName(component);

        const visibleProps = this._getVisibleProps(properties);
        const orderedProps = FlowPropertiesUtils.getOrderedProps(visibleProps);

        const categoriesWithProps = this._getCategoriesWithProps(orderedProps, component.categories);
        const propsWithoutCategory = this._getPropsWithoutCategories(orderedProps, component.categories);

        const errors = controllers.ui.flowValidation.validateFlowProperties(properties, block);

        return (
            <div>
                <Section className={styles.componentNameSection}>
                    <ComponentInfo name={componentName} description={component.description} />
                    <div
                        className={cx('iconDoc')}
                        onMouseDown={(e:React.MouseEvent<HTMLElement>) => this._onClickIconDoc(e, componentName)}
                    >
                        <WSPIcon
                            name={WSPIcons.visualDesigner.spreadsheet.Help}
                            size={WSPIconSize.medium20}
                        />
                    </div>
                </Section>
                {
                    component.name === 'ReadBigQueryComponent' && //todo: introduce a setting in a yaml file
                    this._renderFileDropzone(block)
                }
                <Section>
                    {
                        categoriesWithProps.map(({key, label, props}) =>
                        {
                            const title = <InlineLabel text={label} />;
                            const reactKey = `${block.id}-${key}`;
                            const renderedProps = props.map((prop) => this._renderProp(prop, block, errors));

                            if( !label )
                            {
                                return renderedProps;
                            }

                            return (
                                <Row key={reactKey}>
                                    <Col size={4}>
                                        <Collapse target={title} defaultIsOpen={true}>
                                            {renderedProps}
                                        </Collapse>
                                    </Col>
                                </Row>
                            );
                        })
                    }
                    {
                        propsWithoutCategory.map((prop) => this._renderProp(prop, block, errors))
                    }
                </Section>
            </div>
        );
    }

    private _renderProp(prop:FlowProperty, block:FlowBlock, errors:FlowValidationError[])
    {
        const {definition} = prop;
        const {name: propName} = definition;
        const WidgetComponent = FlowPropertiesUtils.getWidgetComponent(definition);
        const reactKey = `${block.id}-${prop.name}`;

        const fppController = controllers.ui.flowPropertiesPanel;
        const frameColumnNames = this._getFrameColumnNames(block);
        const dirtyProps = fppController.getDirtyProperties();
        const isDirty = dirtyProps[fppController.getDirtyPropertyKey(block.id, propName)];
        const error = FlowPropertiesUtils.getPropError(errors, propName);

        return (
            <Row key={reactKey}>
                <Col size={4}>
                    {this._renderPropBlockLabel(prop)}
                    <WidgetComponent
                        prop={prop}
                        block={block}
                        inlineLabel={this._renderPropInlineLabel(prop)}
                        error={error}
                        isDirty={isDirty !== undefined ? isDirty : false}
                        frameColumnNames={frameColumnNames}
                        onChange={this._getPropValueChangeHandler(prop, block)}
                        onMetaChange={this._getPropMetaChangeHandler(prop, block)}
                        onRef={this._refHandlers.forWidget(propName)}
                    />
                </Col>
            </Row>
        );
    }

    private _renderPropBlockLabel(prop:FlowProperty)
    {
        const {definition} = prop;
        const {label, description} = definition;
        const widget = controllers.ui.flowPropertiesPanel.getWidgetName(definition);

        if( widget === PropertyWidgetName.Checkbox )
        {
            return null;
        }

        return (
            <PropertyBlockLabel label={label} description={description} />
        );
    }

    private _renderPropInlineLabel(prop:FlowProperty)
    {
        const {definition} = prop;
        const widget = controllers.ui.flowPropertiesPanel.getWidgetName(definition);

        if( widget === PropertyWidgetName.Checkbox )
        {
            return FlowPropertiesUtils.getPropInlineLabel(prop);
        }

        return null;
    }

    private _renderNoComponentSelected()
    {
        return (
            <Section>
                Select a block to edit its properties.
            </Section>
        );
    }

    private _renderMultipleComponentsSelected()
    {
        return (
            <Section>
                Select a single block to edit its properties.
            </Section>
        );
    }

    private _renderFileDropzone(block:FlowBlock)
    {
        const {isDraggingOver, isLoading, errorMsg} = this._state.fileDropzoneCtx;

        return (
            <Section>
                <div
                    className={cx('fileDropzone', {isDraggingOver, isInvalid: !!errorMsg})}
                    onClick={this._handleFileClick}
                    onDragOver={this._handleFileDragOver}
                    onDragLeave={this._handleFileDragLeave}
                    onDrop={this._getFileDropHandler(block)}
                >
                    <input
                        type={'file'}
                        value={''}
                        ref={this._refHandlers.fileInput}
                        className={cx('fileInput')}
                        onChange={this._getFileInputChangeHandler(block)}
                    />
                    <div className={cx('fileMessage')}>
                        {isLoading ? 'Uploading...' : 'To quickly fill out all properties, drop a JSON file here or click to upload one'}
                    </div>
                </div>
            </Section>
        );
    }

    private _getPropValueChangeHandler(prop:FlowProperty, block:FlowBlock)
    {
        const fppController = controllers.ui.flowPropertiesPanel;

        const {definition} = prop;
        const {name: propName} = definition;
        const dirtyPropKey = fppController.getDirtyPropertyKey(block.id, propName);

        return action((value:any) =>
        {
            prop.value = value;
            fppController.setDirtyProperty(dirtyPropKey);
        });
    }

    private _getPropMetaChangeHandler(prop:FlowProperty, block:FlowBlock)
    {
        return action((meta:any) =>
        {
            prop.meta = meta;
        });
    }

    private _handleFileClick = (event:React.MouseEvent<HTMLDivElement>) =>
    {
        if( this._fileInputRef )
        {
            this._fileInputRef.click();
        }
    };

    @action
    private _handleFileDragOver = (event:React.DragEvent<HTMLDivElement>) =>
    {
        event.preventDefault();

        const {fileDropzoneCtx} = this._state;

        fileDropzoneCtx.isDraggingOver = true;
    };

    @action
    private _handleFileDragLeave = (event:React.DragEvent<HTMLDivElement>) =>
    {
        event.preventDefault();

        const {fileDropzoneCtx} = this._state;

        fileDropzoneCtx.isDraggingOver = false;
    };

    private _getFileDropHandler = (block:FlowBlock) =>
    {
        return action((event:React.DragEvent<HTMLDivElement>) =>
        {
            event.preventDefault();

            const {fileDropzoneCtx} = this._state;

            fileDropzoneCtx.isDraggingOver = false;

            this._processFiles(block, event.dataTransfer.files);
        });
    };

    private _getFileInputChangeHandler = (block:FlowBlock) =>
    {
        return action((event:React.ChangeEvent<HTMLInputElement>) =>
        {
            this._processFiles(block, event.target.files);
        });
    };

    private _processFiles(block:FlowBlock, files:FileList)
    {
        const {fileDropzoneCtx} = this._state;

        fileDropzoneCtx.errorMsg = null;

        if( files.length !== 1 )
        {
            fileDropzoneCtx.errorMsg = 'Select a single file';
            return;
        }

        const file:File = files[0];

        if( !file.type.match('application/json') )
        {
            fileDropzoneCtx.errorMsg = 'Select a json file';
            return;
        }

        const reader:FileReader = new FileReader();

        reader.onloadstart = action((event:any) =>
        {
            fileDropzoneCtx.isLoading = true;
        });

        reader.onload = action((event:any) =>
        {
            try
            {
                const data = JSON.parse(event.target.result);

                const visibleProps = this._getVisibleProps(block.properties);

                visibleProps.forEach((prop:FlowProperty) =>
                {
                    const value = data[prop.name];

                    if( value !== undefined )
                    {
                        const changePropValue = this._getPropValueChangeHandler(prop, block);

                        changePropValue(value);
                    }
                });

            }
            catch( error )
            {
                fileDropzoneCtx.errorMsg = 'Select a json file';
            }

            fileDropzoneCtx.isLoading = false;
        });

        reader.onerror = action((event:any) =>
        {
            reader.abort();
            fileDropzoneCtx.isLoading = false;
        });

        reader.readAsText(file);
    }

    private _getFrameColumnNames(block:FlowBlock):string[]
    {
        const vdController = controllers.ui.visualData;
        const blockStreams = VisualStreamIds.forBlock(block);
        const inputStreamer = vdController.getStreamer(blockStreams.input);

        return inputStreamer ? inputStreamer.getGridModel().getColumnKeys() : [];
    }

    private _getCategoriesWithProps(props:FlowProperty[], categories:CloudCategory[]):CategoryWithProps[]
    {
        return FlowPropertiesUtils.getOrderedCategories(categories || [])
            .map(({key, label}) => ({
                label,
                key,
                props: props.filter(({definition}) => definition.category === key)
            }))
            .filter(({props}) => props.length > 0);
    }

    private _getPropsWithoutCategories(props:FlowProperty[], categories:CloudCategory[]):FlowProperty[]
    {
        const categoryKeys = categories.map(({key}) => key);

        return props.filter(({definition}) => (
            categoryKeys.indexOf(definition.category) === -1
        ));
    }

    private _getVisibleProps(properties:FlowProperty[]):FlowProperty[]
    {
        const visibilityMap = FlowPropertiesUtils.getPropVisibilityMap(properties);

        return properties.filter((prop:FlowProperty) => visibilityMap[prop.name]);
    }
}
